import { ApiConfigType, ApiTourType, TourType } from "@/types";
import {
  ApiTourAccessCode,
  ApiUnitType,
  UnitAccessCode,
  UnitStatusType,
  UnitType,
} from "@/types";

import { tourStatus } from "./tour-status";

export function normalizeTour(
  tour: ApiTourType,
  config: ApiConfigType | { access_code_early_access: any }
): TourType {
  const normalizedTour = {
    codesAdded: tour.codes_added,
    communityMap: tour.community_map_image_url,
    deletedAt: tour.deleted_at,
    geolocationRequired: tour.geolocation_required,
    hasCommunityAccessCodes: tour.has_community_access_codes,
    hasError: checkForAccessErrors(tour),
    id: tour.id,
    identityVerificationResult: tour.identity_verification_result,
    isFinalized: tour.is_finalized,
    requireIdVerification: tour.require_id_verification,
    scheduledEnd: tour.scheduled_end,
    scheduledStart: tour.scheduled_start,
    scheduledStartNow: tour.scheduled_start_now,
    status: "pending" as const,
    token: tour.token,
    units: units(tour),
    source: tour.source,
  };

  return {
    ...normalizedTour,
    status: tourStatus(normalizedTour, config.access_code_early_access || 0),
  };
}

function units(tour: ApiTourType): UnitType[] {
  const units = tour.units || [];
  const accessCodes: Array<
    UnitAccessCode & {
      status: UnitStatusType;
    }
  > | null = normalizeAccessCodes(
    tour.tour_access_codes,
    tour.unit_access_codes
  );

  return units.map((unit: ApiUnitType) => {
    const accessCode = accessCodes?.find((code) => code.unitId === unit.id);

    return {
      name: unit.tour_marketing_name || unit.marketing_name,
      id: unit.id,
      remoteId: unit.remote_id,
      streetAddress1: unit.street_address_1,
      streetAddress2: unit.street_address_2,
      city: unit.city,
      state: unit.state,
      zip: unit.zip,
      lat: unit.lat,
      lng: unit.lng,
      canBeToured: unit.can_be_toured,
      building: unit.building,
      minMarketingRent: unit.min_marketing_rent,
      maxMarketingRent: unit.max_marketing_rent,
      bedrooms: unit.beds,
      sqft: unit.sqft,
      baths: unit.baths,
      floor: unit.floor,
      enableAccessibilityIcon: unit.enable_accessibility_icon,
      imageUrl: unit.image_url,
      lockType: unit.lock_type,
      clickToUnlockAvailable: unit.click_to_unlock_available,
      accessCode: accessCode
        ? {
            code: accessCode?.code,
            deviceType: accessCode?.deviceType,
            unitId: accessCode?.unitId,
            accessControlDevicePresent: accessCode?.accessControlDevicePresent,
            make: accessCode?.make,
          }
        : null,
      status: accessCode?.status,
    };
  });
}

function normalizeAccessCodes(
  tourAccessCodes: ApiTourAccessCode[],
  unitAccessCodes: any
): Array<
  UnitAccessCode & {
    status: UnitStatusType;
  }
> {
  const nonZwaveCodes = unitAccessCodes
    ? unitAccessCodes.map((unitAccessCode: any) => {
        return {
          accessControlDevicePresent: !!unitAccessCode.access_control_device,
          code: unitAccessCode.code,
          deviceType: getThirdPartyDeviceType(unitAccessCode.credential_type),
          status: unitAccessCode.error ? "error" : "added",
          unitId: unitAccessCode.access_control_device?.unit_id,
          make: unitAccessCode.access_control_device.make,
        };
      })
    : [];

  const zwaveCodes = tourAccessCodes
    ? tourAccessCodes.map((tourAccessCode: ApiTourAccessCode) => {
        return {
          code: tourAccessCode.code,
          deviceType: getDeviceType(tourAccessCode),
          status: tourAccessCode.status,
          unitId: tourAccessCode.unit_id,
        };
      })
    : [];

  return [...nonZwaveCodes, ...zwaveCodes];
}

function getThirdPartyDeviceType(credentialType: string) {
  switch (credentialType) {
    case "Alloy Access BLE":
      return "bluetooth";
    case "code":
      return "lockbox";
    default:
      return "unknown";
  }
}

function getDeviceType(tourAccessCode: ApiTourAccessCode) {
  const device = getDevice(tourAccessCode);

  switch (device?.zw_mf_id) {
    case "144":
      return "kwikset";
    case "297":
      return "yale";
    // AlloyDeadBolt is a whitelabel of the kaadas lock
    case "541":
      return "kaadas";
    // AlloyDeadBolt+ is a whitelable of the tlj lock
    case "1097":
      return "tlj";
    default:
      return "zwave";
  }
}

function getDevice(tourAccessCode: ApiTourAccessCode) {
  if (tourAccessCode.access_codes[0]?.device) {
    return tourAccessCode.access_codes[0]?.device;
  } else if (tourAccessCode.emergency_access_code?.access_codes[0]?.device) {
    return tourAccessCode.emergency_access_code.access_codes[0]?.device;
  } else {
    return null;
  }
}

function checkForAccessErrors(tour: ApiTourType): boolean {
  const unitAccessError = tour.unit_access_codes?.find((el) => !!el.error);
  return !!unitAccessError;
}
